// Badge catalog + pure award logic. Earned server-side from trusted, server-
// computed ProfileStats (never client-claimed), then rendered on the profile.
// No secrets, no Node APIs — safe in the browser bundle.
import type { BadgeView, ProfileStats } from "./profile";

export type BadgeId =
  | "first-race"
  | "ten-races"
  | "centurion"
  | "flawless"
  | "sharpshooter"
  | "speed-demon"
  | "scholar"
  | "explorer"
  | "daily-3"
  | "daily-7"
  | "daily-30";

export interface Badge {
  id: BadgeId;
  name: string;
  emoji: string;
  description: string;
  /** Returns true when the player's stats qualify for this badge. */
  earned: (s: ProfileStats) => boolean;
}

/** Fastest-pace gate for Speed Demon: average answer at or under 3 seconds. */
const SPEED_DEMON_MS = 3000;

/** The catalog, ordered roughly easiest → hardest for display. */
export const BADGES: Badge[] = [
  {
    id: "first-race",
    name: "First Finish",
    emoji: "🏁",
    description: "Cross the finish line for the first time.",
    earned: (s) => s.totalRaces >= 1,
  },
  {
    id: "ten-races",
    name: "Regular Racer",
    emoji: "🏎️",
    description: "Finish 10 races.",
    earned: (s) => s.totalRaces >= 10,
  },
  {
    id: "centurion",
    name: "Centurion",
    emoji: "💪",
    description: "Finish 100 races.",
    earned: (s) => s.totalRaces >= 100,
  },
  {
    id: "flawless",
    name: "Flawless",
    emoji: "💯",
    description: "Finish a race with a perfect 100% accuracy.",
    earned: (s) => s.perfectRaces >= 1,
  },
  {
    id: "sharpshooter",
    name: "Sharpshooter",
    emoji: "🎯",
    description: "Finish 10 perfect races.",
    earned: (s) => s.perfectRaces >= 10,
  },
  {
    id: "speed-demon",
    name: "Speed Demon",
    emoji: "⚡",
    description: "Average under 3 seconds per answer in a race.",
    earned: (s) => s.fastestAvgMs > 0 && s.fastestAvgMs <= SPEED_DEMON_MS,
  },
  {
    id: "scholar",
    name: "Scripture Scholar",
    emoji: "📚",
    description: "Answer 500 questions correctly in all.",
    earned: (s) => s.totalCorrect >= 500,
  },
  {
    id: "explorer",
    name: "Explorer",
    emoji: "🧭",
    description: "Race in 5 different categories.",
    earned: (s) => s.categoriesPlayed >= 5,
  },
  {
    id: "daily-3",
    name: "Daily Devotion",
    emoji: "📅",
    description: "Complete the Daily Challenge 3 days in a row.",
    earned: (s) => s.dailyStreak >= 3,
  },
  {
    id: "daily-7",
    name: "Faithful Week",
    emoji: "📆",
    description: "Complete the Daily Challenge 7 days in a row.",
    earned: (s) => s.dailyStreak >= 7,
  },
  {
    id: "daily-30",
    name: "Faithful Month",
    emoji: "🗓️",
    description: "Complete the Daily Challenge 30 days in a row.",
    earned: (s) => s.dailyStreak >= 30,
  },
];

const BY_ID = new Map<BadgeId, Badge>(BADGES.map((b) => [b.id, b]));

export function badgeById(id: string): Badge | undefined {
  return BY_ID.get(id as BadgeId);
}

/** Every badge id the stats currently qualify for. The server diffs this against
 *  what's already recorded to find newly-earned badges (awards are idempotent). */
export function evaluateBadges(stats: ProfileStats): BadgeId[] {
  return BADGES.filter((b) => b.earned(stats)).map((b) => b.id);
}

/** Project the full catalog into views, marking which are earned (and when). */
export function toBadgeViews(earnedAt: Map<string, string>): BadgeView[] {
  return BADGES.map((b) => ({
    id: b.id,
    name: b.name,
    emoji: b.emoji,
    description: b.description,
    earned: earnedAt.has(b.id),
    earnedAt: earnedAt.get(b.id) ?? null,
  }));
}

/** Views for a specific set of badge ids (e.g. the ones just earned). */
export function badgeViewsFor(ids: string[]): BadgeView[] {
  return ids
    .map((id) => badgeById(id))
    .filter((b): b is Badge => Boolean(b))
    .map((b) => ({
      id: b.id,
      name: b.name,
      emoji: b.emoji,
      description: b.description,
      earned: true,
      earnedAt: null,
    }));
}
